/**
 * AudioWaveformPlayer -- Shared waveform visualization with playhead cursor and click-to-seek.
 *
 * Bars use `flex-1` to fill the container width, ensuring that percentage-based
 * cursor positioning and seek calculations align correctly with the bar positions.
 *
 * Props:
 *   bars          - pre-computed amplitude bars from useWaveform
 *   barCount      - total expected bars (used for empty-state rendering)
 *   height        - waveform area height in px
 *   accentColor   - bar fill color
 *   barRadius     - border-radius for bars (default 2)
 *   minBarHeight  - minimum bar height % (default 15)
 *   progress      - 0-1 playback progress (for played-bar coloring + cursor)
 *   showCursor    - show playhead line (default false)
 *   onSeek        - click-to-seek callback receiving 0-1 fraction
 *   className     - additional container className
 */

import { memo, useCallback } from 'react'

export interface AudioWaveformPlayerProps {
  bars: number[]
  barCount: number
  height: number
  accentColor: string
  barRadius?: number
  minBarHeight?: number
  progress?: number
  showCursor?: boolean
  onSeek?: (fraction: number) => void
  className?: string
}

export const AudioWaveformPlayer = memo(function AudioWaveformPlayer({
  bars,
  barCount,
  height,
  accentColor,
  barRadius = 2,
  minBarHeight = 15,
  progress = 0,
  showCursor = false,
  onSeek,
  className,
}: AudioWaveformPlayerProps) {
  const playedBars = Math.floor(progress * barCount)

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onSeek) return
      const rect = e.currentTarget.getBoundingClientRect()
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      onSeek(fraction)
    },
    [onSeek],
  )

  return (
    <div
      className={`relative flex items-end gap-px ${onSeek ? 'cursor-pointer' : ''} ${className ?? ''}`}
      style={{ height }}
      onClick={onSeek ? handleClick : undefined}
      data-testid="waveform-player"
    >
      {bars.length > 0
        ? bars.map((amp, i) => (
            <div
              key={i}
              data-testid="waveform-bar"
              className="flex-1"
              style={{
                height: `${Math.max(minBarHeight, amp * 100)}%`,
                borderRadius: barRadius,
                background: accentColor,
                opacity: i < playedBars ? 0.8 : 0.4,
                transition: i === playedBars ? 'opacity 100ms' : undefined,
              }}
            />
          ))
        : Array.from({ length: barCount }, (_, i) => (
            <div
              key={i}
              data-testid="waveform-bar"
              className="flex-1"
              style={{
                height: `${minBarHeight}%`,
                borderRadius: barRadius,
                background: accentColor,
                opacity: 0.2,
              }}
            />
          ))}
      {showCursor && progress > 0 && (
        <div
          className="absolute top-0 bottom-0 w-[1.5px] pointer-events-none transition-[left] duration-100"
          style={{ left: `${progress * 100}%`, background: accentColor }}
          data-testid="waveform-cursor"
        />
      )}
    </div>
  )
})

AudioWaveformPlayer.displayName = 'AudioWaveformPlayer'
